import { VALUES_TO_REDACT_REGEX } from './keys';

/**
 * Strips query-param keys that match VALUES_TO_REDACT_REGEX from a URL string, which may be
 * absolute, root-relative, non-rooted-relative, or protocol-relative (`//host/path`). Used by
 * `redactForLogging` when the object walk hits a string leaf under a URL-shaped key (see
 * `URL_KEY_REGEX`), since a URL is a plain string and therefore invisible to the object-key
 * traversal on its own.
 *
 * Operates on the raw string rather than parsing it with `new URL(...)`: reconstructing the URL
 * from a parsed object's parts (origin/pathname/search/hash) can't tell a root-relative reference
 * apart from a non-rooted one (both parse to the same pathname against a placeholder base) and
 * loses a protocol-relative authority entirely, so this instead splits on the first `?`/`#` and
 * rewrites only the userinfo password, the query, and `key=value` fragments, leaving everything else
 * byte-for-byte as given.
 */
// greedy up to the last `@` in the authority, like URL parsers, so an unencoded `@` in the password doesn't leak its tail
const USERINFO_PASSWORD_REGEX = /^((?:[a-z][a-z\d+.-]*:)?\/\/[^/?#@:]*):[^/?#]*@/i;

function redactParams(query: string): string | undefined {
	const params = new URLSearchParams(query);
	let changed = false;
	for (const key of [...params.keys()]) {
		if (VALUES_TO_REDACT_REGEX.test(key)) {
			params.set(key, '[Redacted]');
			changed = true;
		}
	}
	return changed ? params.toString() : undefined;
}

export function redactUrlParams(url: string): string {
	const withUserinfoRedacted = url.replace(USERINFO_PASSWORD_REGEX, '$1:[Redacted]@');

	const hashIndex = withUserinfoRedacted.indexOf('#');
	const beforeHash = hashIndex === -1 ? withUserinfoRedacted : withUserinfoRedacted.slice(0, hashIndex);
	let hash = hashIndex === -1 ? '' : withUserinfoRedacted.slice(hashIndex);
	// OAuth implicit-flow tokens (`#access_token=…`) live in the fragment
	if (hash.includes('=')) {
		const redactedHash = redactParams(hash.slice(1));
		if (redactedHash !== undefined) hash = `#${redactedHash}`;
	}

	const queryIndex = beforeHash.indexOf('?');
	if (queryIndex === -1) return `${beforeHash}${hash}`;
	const redactedQuery = redactParams(beforeHash.slice(queryIndex + 1));
	if (redactedQuery === undefined) return `${beforeHash}${hash}`;
	return `${beforeHash.slice(0, queryIndex)}?${redactedQuery}${hash}`;
}
